/**
 * SAVENE — Product Detail Page (PDP) controller
 * Responsibilities:
 * - Gallery: thumbnail click, prev/next, lightbox zoom
 * - Variant switching: Dourado/Ródio/Tamanho → updates image/price/stock/URL
 * - Quantity stepper
 * - Add to cart (publish cartUpdate event → drawer opens)
 * - Wishlist (localStorage)
 * - Gift message (localStorage → cart.attributes)
 * - CEP shipping calc via ViaCEP + Shopify shipping_rates API
 * - Recently viewed (localStorage, renders last 6)
 * - Recommendations via Shopify recommendations API
 */
(function () {
  'use strict';
  const CART_ADD = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const RECENT_KEY = 'sv_recent_products';
  const WISH_KEY = 'sv_wishlist';
  const MAX_RECENT = 12;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }
  function money(cents) {
    return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
  function toast(msg, type) {
    const t = document.createElement('div');
    t.className = 'sv-pdp-toast' + (type ? ' sv-pdp-toast--' + type : '');
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add('is-open'));
    setTimeout(() => { t.classList.remove('is-open'); setTimeout(() => t.remove(), 300); }, 4000);
  }

  // ============================================================
  // GALLERY
  // ============================================================
  function initGallery() {
    const gal = $('[data-sv-gallery]');
    if (!gal) return;

    const thumbs = $$('[data-sv-thumb]', gal);
    const slides = $$('[data-sv-slide]', gal);
    const counter = $('[data-sv-counter]', gal);
    const stage = $('[data-sv-stage]', gal);
    const navPrev = $('[data-sv-gal-prev]', gal);
    const navNext = $('[data-sv-gal-next]', gal);
    const total = slides.length;

    function show(i) {
      thumbs.forEach(t => t.classList.remove('is-active'));
      slides.forEach(s => s.classList.remove('is-active'));
      if (thumbs[i]) thumbs[i].classList.add('is-active');
      if (slides[i]) slides[i].classList.add('is-active');
      if (counter) counter.textContent = (i + 1) + ' / ' + total;
      gal.dataset.svIdx = i;
    }

    thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
    if (navPrev) navPrev.addEventListener('click', () => {
      const i = (parseInt(gal.dataset.svIdx, 10) || 0);
      show((i - 1 + total) % total);
    });
    if (navNext) navNext.addEventListener('click', () => {
      const i = (parseInt(gal.dataset.svIdx, 10) || 0);
      show((i + 1) % total);
    });

    // Lightbox
    const lb = $('#sv-pdp-lightbox');
    if (lb && stage) {
      stage.addEventListener('click', () => {
        const active = slides.find(s => s.classList.contains('is-active'));
        if (!active) return;
        const srcHi = active.dataset.svSrcHi || active.src;
        const img = $('[data-sv-lb-img]', lb);
        if (img) img.src = srcHi;
        lb.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });
      $$('[data-sv-lb-close]', lb).forEach(b => b.addEventListener('click', closeLb));
      lb.addEventListener('click', e => { if (e.target === lb) closeLb(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') closeLb(); });
    }
    function closeLb() {
      if (lb) lb.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    // Swipe mobile
    let touchStartX = 0;
    if (stage) {
      stage.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) < 50) return;
        const i = parseInt(gal.dataset.svIdx, 10) || 0;
        show(dx > 0 ? (i - 1 + total) % total : (i + 1) % total);
      }, { passive: true });
    }
  }

  // ============================================================
  // VARIANT SWITCHING
  // ============================================================
  function initVariants() {
    const buy = $('[data-sv-buy]');
    if (!buy) return;
    const productData = window.svProduct || null;
    if (!productData) return;

    const selectedOptions = [];
    // Initialize from current variant (URL or first available)
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    let currentVariant = productData.variants.find(v => String(v.id) === urlVariant);
    if (!currentVariant) currentVariant = productData.variants.find(v => v.available) || productData.variants[0];
    if (currentVariant && currentVariant.options) {
      currentVariant.options.forEach((val, i) => selectedOptions[i] = val);
    }

    function findVariant(opts) {
      return productData.variants.find(v => opts.every((val, i) => v.options[i] === val));
    }
    function isOptionAvailable(optIndex, optValue) {
      return productData.variants.some(v => {
        if (!v.available) return false;
        if (v.options[optIndex] !== optValue) return false;
        for (let i = 0; i < selectedOptions.length; i++) {
          if (i !== optIndex && selectedOptions[i] && v.options[i] !== selectedOptions[i]) return false;
        }
        return true;
      });
    }

    function render() {
      const variant = findVariant(selectedOptions);

      // Update swatches
      $$('[data-sv-sw]', buy).forEach(el => {
        const idx = parseInt(el.dataset.optIndex, 10);
        const val = el.dataset.optValue;
        const available = isOptionAvailable(idx, val);
        el.classList.toggle('is-selected', selectedOptions[idx] === val);
        el.classList.toggle('is-unavailable', !available);
      });
      $$('[data-sv-sz]', buy).forEach(el => {
        const idx = parseInt(el.dataset.optIndex, 10);
        const val = el.dataset.optValue;
        const available = isOptionAvailable(idx, val);
        el.classList.toggle('is-selected', selectedOptions[idx] === val);
        el.classList.toggle('is-unavailable', !available);
      });

      // Update option display value (ex: "Banho: Dourado")
      $$('[data-sv-opt-display]', buy).forEach(el => {
        const idx = parseInt(el.dataset.svOptDisplay, 10);
        el.textContent = selectedOptions[idx] || '';
      });

      if (!variant) {
        // No variant matches
        const cta = $('[data-sv-add]', buy);
        if (cta) { cta.disabled = true; $('[data-sv-cta-tx]', cta).textContent = 'Selecione as opções'; }
        return;
      }

      // Price
      const priceEl = $('[data-sv-price]', buy);
      const compareEl = $('[data-sv-price-compare]', buy);
      const instEl = $('[data-sv-installment]', buy);
      const pixEl = $('[data-sv-pix]', buy);
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (instEl) {
        if (variant.price >= 9000) {
          instEl.textContent = '3x de ' + money(Math.floor(variant.price / 3)) + ' sem juros';
          instEl.hidden = false;
        } else instEl.hidden = true;
      }
      if (pixEl) {
        const pixPrice = Math.round(variant.price * 0.95);
        const pixValEl = $('[data-sv-pix-price]', pixEl);
        if (pixValEl) pixValEl.textContent = money(pixPrice);
      }

      // Image
      if (variant.featured_image && variant.featured_image.src) {
        const gal = $('[data-sv-gallery]');
        if (gal) {
          const slides = $$('[data-sv-slide]', gal);
          const match = slides.findIndex(s => {
            const src = s.src || '';
            return src.includes(variant.featured_image.id) || src === variant.featured_image.src;
          });
          if (match >= 0) {
            const thumbs = $$('[data-sv-thumb]', gal);
            thumbs.forEach(t => t.classList.remove('is-active'));
            slides.forEach(s => s.classList.remove('is-active'));
            if (thumbs[match]) thumbs[match].classList.add('is-active');
            if (slides[match]) slides[match].classList.add('is-active');
            gal.dataset.svIdx = match;
          }
        }
      }

      // Stock state
      const ctaBtn = $('[data-sv-add]', buy);
      const ctaLabel = ctaBtn ? $('[data-sv-cta-tx]', ctaBtn) : null;
      const lowEl = $('[data-sv-low-stock]', buy);
      if (variant.available) {
        if (ctaBtn) { ctaBtn.disabled = false; }
        if (ctaLabel) ctaLabel.textContent = 'Adicionar à sacola';
      } else {
        if (ctaBtn) { ctaBtn.disabled = true; }
        if (ctaLabel) ctaLabel.textContent = 'Esgotado';
      }
      if (lowEl && variant.inventory_quantity && variant.inventory_management) {
        const qty = variant.inventory_quantity;
        if (qty > 0 && qty <= 3) {
          lowEl.hidden = false;
          lowEl.textContent = (qty === 1 ? 'Última peça disponível' : 'Últimas ' + qty + ' peças');
        } else {
          lowEl.hidden = true;
        }
      }

      // Update URL without reload
      if (history.replaceState) {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', variant.id);
        history.replaceState({ svVariant: variant.id }, '', u.pathname + u.search);
      }

      buy.dataset.variantId = variant.id;
    }

    // Bind option buttons
    $$('[data-sv-sw], [data-sv-sz]', buy).forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn.classList.contains('is-unavailable')) return;
        const idx = parseInt(btn.dataset.optIndex, 10);
        const val = btn.dataset.optValue;
        selectedOptions[idx] = val;
        render();
      });
    });

    // Quantity
    const qtyInput = $('[data-sv-qty-input]', buy);
    $$('[data-sv-qty-btn]', buy).forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.svQtyBtn;
        if (!qtyInput) return;
        let n = parseInt(qtyInput.value, 10) || 1;
        n = dir === 'up' ? n + 1 : Math.max(1, n - 1);
        qtyInput.value = n;
      });
    });

    // Initial render
    render();

    // Gift toggle
    const gift = $('[data-sv-gift]', buy);
    if (gift) {
      const giftToggle = $('[data-sv-gift-toggle]', gift);
      const giftText = $('[data-sv-gift-text]', gift);
      const giftCounter = $('[data-sv-gift-counter]', gift);
      if (giftToggle) {
        giftToggle.addEventListener('change', () => {
          gift.dataset.open = giftToggle.checked ? '1' : '';
        });
      }
      if (giftText && giftCounter) {
        const update = () => { giftCounter.textContent = (200 - giftText.value.length) + ' caracteres restantes'; };
        giftText.addEventListener('input', update);
        update();
      }
    }

    // Add to cart
    const addBtn = $('[data-sv-add]', buy);
    if (addBtn) {
      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (addBtn.disabled) return;
        const variantId = buy.dataset.variantId;
        const qty = parseInt($('[data-sv-qty-input]', buy).value, 10) || 1;
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;

        // Collect gift attributes if checked
        const attributes = {};
        const gt = $('[data-sv-gift-toggle]', buy);
        if (gt && gt.checked) {
          attributes.is_gift = 'yes';
          const gm = $('[data-sv-gift-text]', buy);
          if (gm && gm.value) attributes.gift_message = gm.value.substring(0, 200);
          const gnf = $('[data-sv-gift-nf]', buy);
          if (gnf && gnf.checked) attributes.hide_invoice = 'yes';
        }

        try {
          const res = await fetch(CART_ADD + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
          });
          if (!res.ok) {
            let msg = 'Não conseguimos adicionar. Tente de novo.';
            try { const d = await res.json(); if (d.description) msg = d.description; } catch(_) {}
            throw new Error(msg);
          }
          const cartData = await res.json();

          // Update cart attributes if gift
          if (Object.keys(attributes).length > 0) {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ attributes })
            });
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sv-pdp', productVariantId: variantId, cartData });
          } else {
            const drawer = $('#sv-dr');
            if (drawer) {
              drawer.classList.add('is-open');
              document.body.style.overflow = 'hidden';
              fetch('/cart.js').then(r => r.json()).then(c => {
                const badge = $('.hdr__badge'); if (badge) badge.textContent = c.item_count;
              }).catch(() => {});
            }
          }
          toast('Adicionado à sacola', 'ok');
        } catch (err) {
          toast(err.message || 'Erro ao adicionar', 'err');
        } finally {
          addBtn.classList.remove('is-loading');
          addBtn.disabled = false;
        }
      });
    }

    // Wishlist
    const wishBtn = $('[data-sv-wish]', buy);
    if (wishBtn) {
      const handle = productData.handle;
      const getWish = () => { try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch(_) { return []; } };
      const setWish = (arr) => { try { localStorage.setItem(WISH_KEY, JSON.stringify(arr)); } catch(_) {} };
      const updateBtn = () => {
        const wish = getWish();
        const isWished = wish.includes(handle);
        $('[data-sv-wish-tx]', wishBtn).textContent = isWished ? 'Salvo na lista' : 'Adicionar à lista';
        wishBtn.classList.toggle('is-wished', isWished);
      };
      updateBtn();
      wishBtn.addEventListener('click', (e) => {
        e.preventDefault();
        let wish = getWish();
        if (wish.includes(handle)) {
          wish = wish.filter(h => h !== handle);
          toast('Removido da lista');
        } else {
          wish.push(handle);
          toast('Salvo na lista', 'ok');
        }
        setWish(wish);
        updateBtn();
      });
    }
  }

  // ============================================================
  // CEP SHIPPING CALC
  // ============================================================
  function initCEP() {
    const wrap = $('[data-sv-cep]');
    if (!wrap) return;
    const input = $('[data-sv-cep-field]', wrap);
    const btn = $('[data-sv-cep-btn]', wrap);
    const result = $('[data-sv-cep-result]', wrap);
    if (!input || !btn || !result) return;

    input.addEventListener('input', () => {
      let v = input.value.replace(/\D/g, '').slice(0, 8);
      if (v.length > 5) v = v.slice(0, 5) + '-' + v.slice(5);
      input.value = v;
    });

    async function calc() {
      const cep = input.value.replace(/\D/g, '');
      if (cep.length !== 8) {
        result.innerHTML = '<span class="sv-buy__cep-error">CEP inválido. Tente novamente.</span>';
        return;
      }
      btn.disabled = true;
      result.innerHTML = '<em>Calculando frete...</em>';

      try {
        // 1) Validate CEP via ViaCEP
        const cepRes = await fetch('https://viacep.com.br/ws/' + cep + '/json/');
        const cepData = await cepRes.json();
        if (cepData.erro) throw new Error('CEP não encontrado. Verifique o número.');

        const location = (cepData.localidade || '') + (cepData.uf ? ' — ' + cepData.uf : '');

        // 2) Get shipping rates via Shopify public API (requires preliminary cart setup)
        // Simplified: show static estimate based on region + link to full calc at checkout
        const freeMsg = 'Frete grátis acima de <strong>R$ 299</strong>.';
        let html = '<strong>' + location + '</strong><br>';
        html += freeMsg;
        html += '<br><small>Valor exato e prazo são calculados no checkout.</small>';
        result.innerHTML = html;

        // Store CEP in localStorage for next visits
        try { localStorage.setItem('sv_cep', cep); } catch(_) {}
      } catch (err) {
        result.innerHTML = '<span class="sv-buy__cep-error">' + (err.message || 'Erro ao calcular frete.') + '</span>';
      } finally {
        btn.disabled = false;
      }
    }
    btn.addEventListener('click', (e) => { e.preventDefault(); calc(); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); calc(); } });

    // Restore CEP from localStorage
    try {
      const saved = localStorage.getItem('sv_cep');
      if (saved && saved.length === 8) {
        input.value = saved.slice(0, 5) + '-' + saved.slice(5);
      }
    } catch(_) {}
  }

  // ============================================================
  // RECENTLY VIEWED
  // ============================================================
  function initRecentlyViewed() {
    const recent = $('[data-sv-recent]');
    const productData = window.svProduct;

    // Add current product to recent (on every PDP load)
    if (productData && productData.handle) {
      try {
        let list = JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
        list = list.filter(h => h !== productData.handle);
        list.unshift(productData.handle);
        list = list.slice(0, MAX_RECENT);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      } catch(_) {}
    }

    if (!recent) return;
    const grid = $('[data-sv-recent-grid]', recent);
    if (!grid) return;

    // Render last 6 excluding current product
    let list;
    try { list = JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch(_) { list = []; }
    const toRender = list
      .filter(h => productData ? h !== productData.handle : true)
      .slice(0, 6);

    if (toRender.length === 0) { recent.hidden = true; return; }

    // Fetch each as mini card via /products/{handle}.js
    Promise.all(toRender.map(h =>
      fetch('/products/' + h + '.js').then(r => r.ok ? r.json() : null).catch(() => null)
    )).then(products => {
      const valid = products.filter(p => p);
      if (valid.length === 0) { recent.hidden = true; return; }
      grid.innerHTML = valid.map(p => `
        <a href="${p.url}" class="sv-recent__c">
          <div style="aspect-ratio:3/4;overflow:hidden;background:var(--bg-alt);border-radius:2px;margin-bottom:8px">
            ${p.featured_image ? `<img src="${p.featured_image}" alt="${p.title}" loading="lazy" style="width:100%;height:100%;object-fit:cover">` : ''}
          </div>
          <p style="font-size:0.8125rem;color:var(--text);margin-bottom:2px;line-height:1.3">${p.title}</p>
          <p style="font-size:0.875rem;font-weight:600;color:var(--text)">R$ ${(p.price/100).toFixed(2).replace('.',',')}</p>
        </a>
      `).join('');
      recent.hidden = false;
    });
  }

  // ============================================================
  // RECOMMENDATIONS (Shopify Product Recommendations API)
  // ============================================================
  function initRecommendations() {
    const wrap = $('[data-sv-recs]');
    if (!wrap) return;
    const productId = wrap.dataset.productId;
    if (!productId) return;
    const grid = $('[data-sv-recs-grid]', wrap);
    if (!grid) return;

    const sectionId = wrap.dataset.sectionId || 'main';
    const url = '/recommendations/products?section_id=' + sectionId + '&product_id=' + productId + '&limit=4&intent=related';

    fetch(url).then(r => r.text()).then(html => {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-sv-recs-grid]');
      if (fresh && fresh.innerHTML.trim()) {
        grid.innerHTML = fresh.innerHTML;
        if (typeof window.svInitProductCards === 'function') window.svInitProductCards();
      } else {
        wrap.hidden = true;
      }
    }).catch(() => { wrap.hidden = true; });
  }

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    initGallery();
    initVariants();
    initCEP();
    initRecommendations();
    initRecentlyViewed();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
